import { useEffect } from 'react';

import styled from '@emotion/styled';
import toast from 'react-hot-toast';

import { PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { Card, CardHead, CardTitle, Hint, Scroll, Select, SimpleTable } from '../../../components/Layout/PagePieces';
import useLimitsReview from '../../../hooks/dashboard/limits/useLimitsReview';
import { LIMIT_LABELS } from '../../../models/limits/limits.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

const money = (v: string | null) => (v == null ? '—' : formatNaira(Number(v)));
const BILL_LABELS: Record<string, string> = {
  AIRTIME: 'Airtime',
  MOBILE_DATA: 'Data',
  CABLE_TV: 'Cable TV',
  ELECTRICITY: 'Electricity',
};
const monthLabel = (month: string) =>
  new Date(`${month}-01T12:00:00`).toLocaleDateString('en-NG', { month: 'long', year: 'numeric' });

// Monthly limits review (limits spec D32): built on the 1st for last month.
// Flags only suggest; changing a limit is still the form above.
const LimitsReviewCard = () => {
  const { review, running, fetchReview, runReview } = useLimitsReview();

  useEffect(() => {
    fetchReview();
    // eslint-disable-next-line
  }, []);

  const data = review?.review;

  return (
    <Card>
      <CardHead>
        <CardTitle>Monthly review</CardTitle>
        <Controls>
          {!!review?.months.length && (
            <Select aria-label='Month' value={data?.month ?? ''} onChange={(e) => fetchReview(e.target.value)}>
              {review.months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </Select>
          )}
          <PrimaryWhiteButton
            type='button'
            disabled={running}
            onClick={() => runReview(data?.month, () => toast.success('Review rebuilt'))}
          >
            {running ? 'Building…' : data ? 'Rebuild this month' : 'Build last month'}
          </PrimaryWhiteButton>
        </Controls>
      </CardHead>
      <Hint>
        Built on the 1st of each month. A limit is flagged when more than 1% of a bill type’s attempts or more than 2% of
        active customers were refused. Nothing changes on its own: raise a limit above if the flag makes sense.
      </Hint>

      {!data && <Hint>No review yet. The first one is built on the 1st, or build last month’s now.</Hint>}

      {data && (
        <>
          <Hint>
            {monthLabel(data.month)} · {data.active_users} active customers · {data.refusals} refused (
            {data.new_account_refusals} on new accounts) · built {tableFormatDateTimeNG(data.generated_at)}
            {!data.limits_enabled && ' · limits were switched off, so nothing was refused'}
          </Hint>

          {data.flags.length > 0 ? (
            <Flags>
              {data.flags.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </Flags>
          ) : (
            <Calm>No limit needs a look this month.</Calm>
          )}

          <Scroll>
            <SimpleTable>
              <thead>
                <tr>
                  <th>Bill type</th>
                  <th className='num'>Purchases</th>
                  <th className='num'>99% were under</th>
                  <th className='num'>Largest</th>
                  <th className='num'>One-purchase limit</th>
                  <th className='num'>Refused</th>
                </tr>
              </thead>
              <tbody>
                {data.bill_types.map((b) => (
                  <tr key={b.bill_type} className={b.flag ? 'loss' : ''}>
                    <td>
                      {BILL_LABELS[b.bill_type] ?? b.bill_type}
                      {b.flag && <Badge>Consider raising</Badge>}
                    </td>
                    <td className='num'>{b.purchases}</td>
                    <td className='num'>{money(b.p99)}</td>
                    <td className='num'>{money(b.largest)}</td>
                    <td className='num'>{money(b.limit)}</td>
                    <td className='num'>
                      {b.refused} ({b.refused_share_pct}%)
                    </td>
                  </tr>
                ))}
              </tbody>
            </SimpleTable>
          </Scroll>

          {data.limit_keys.length > 0 && (
            <Scroll>
              <SimpleTable>
                <thead>
                  <tr>
                    <th>Limit</th>
                    <th className='num'>Set at</th>
                    <th className='num'>Refusals</th>
                    <th className='num'>Customers refused</th>
                  </tr>
                </thead>
                <tbody>
                  {data.limit_keys.map((k) => (
                    <tr key={k.limit_key} className={k.flag ? 'loss' : ''}>
                      <td>
                        {LIMIT_LABELS[k.limit_key] ?? k.limit_key}
                        {k.flag && <Badge>Consider raising</Badge>}
                      </td>
                      <td className='num'>{money(k.limit)}</td>
                      <td className='num'>{k.refusals}</td>
                      <td className='num'>
                        {k.users} ({k.users_share_pct}%)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SimpleTable>
            </Scroll>
          )}
        </>
      )}
    </Card>
  );
};

export default LimitsReviewCard;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Flags = styled.ul`
  margin: 0;
  padding: 12px 16px 12px 32px;
  border-radius: 12px;
  background: #fff8e6;
  border: 1px solid #f1d48a;
  color: #6b4a00;
  font-size: 14px;
`;

const Calm = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const Badge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff1d6;
  color: #6b4a00;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;
